/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React from "react";

import { motion } from "framer-motion";
import styled from "styled-components";

import { DEFAULT_THUMBNAIL_WIDTH } from "src/lib/constants";

interface StyleProp {
  style?: React.CSSProperties;
}

export const ViennaLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 50 50" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M26.6489 0C27.801 0.0751733 28.9317 0.228548 30.036 0.454879L30.036 3.93152L30.0357 23.3476L46.5512 23.3475C45.93 15.0949 40.6941 8.13312 33.4226 5.0467L33.4228 1.40386C43.086 4.86838 50 14.125 50 25C50 35.875 43.086 45.1316 33.4228 48.5961L33.4226 44.9533C40.6678 41.878 45.8922 34.9553 46.5443 26.7418L30.0357 26.7416L30.0357 46.0685L30.036 49.5451C28.9317 49.7714 27.801 49.9248 26.6489 50L26.649 47.0168L26.6488 3.34045L26.6489 0ZM23.2619 0.00597759L23.2619 1.28609L23.262 48.3566L23.2619 49.994C10.2656 49.0996 0 38.2512 0 25C0 14.1584 6.87164 4.92522 16.4883 1.43593L16.4882 5.08479C8.78363 8.39622 3.38681 16.0665 3.38681 25C3.38681 35.1927 10.4122 43.741 19.8751 46.047L19.875 5.30538L19.8752 0.473264C20.9791 0.242783 22.1098 0.0852665 23.2619 0.00597759Z"
      fill="currentColor"
    />
  </svg>
);

export const OpenDrillIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13 12C13 12.5523 12.5523 13 12 13H8.5C8.22386 13 8 12.7761 8 12.5C8 12.2239 8.22386 12 8.5 12H12V3H3V6.5C3 6.77614 2.77614 7 2.5 7C2.22386 7 2 6.77614 2 6.5V3C2 2.44771 2.44771 2 3 2H12C12.5523 2 13 2.44771 13 3V12Z"
      fill="currentColor"
    />
    <path d="M5.5 6.5H8.5V9.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2.5 12.5L8.5 6.5" stroke="currentColor" strokeLinecap="round" />
  </svg>
);

export const CreatePage = () => (
  <svg width={DEFAULT_THUMBNAIL_WIDTH} height="205px" viewBox="0 0 120 205" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M61,82 L61,103 L82,103 L82,105 L61,105 L61,126 L59,126 L59,105 L38,105 L38,103 L59,103 L59,82 L61,82 Z"
      fill="currentColor"
    />
  </svg>
);

interface TwitterIconProps {
  height?: string;
  width?: string;
}

export const TwitterIcon = ({ height = "24", width = "20" }: TwitterIconProps) => (
  <svg width={width} height={height} viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.9207 5.48434C10.9207 2.45532 13.3715 0 16.394 0C18.1684 0 19.5853 0.840721 20.5348 2.0608C21.5546 1.85183 22.5135 1.47389 23.3811 0.958283C23.039 2.02947 22.3152 2.93008 21.3715 3.50001L21.3739 3.50555C22.2994 3.39301 23.18 3.14649 24 2.78254L23.998 2.78551C23.4081 3.67046 22.6665 4.45167 21.8141 5.08814C21.8598 5.39742 21.8831 5.70874 21.8831 6.01827C21.8831 12.4821 16.967 20 7.92347 20C5.15379 20 2.57411 19.1872 0.402725 17.7917C-0.00413625 17.5302 -0.122343 16.9879 0.138704 16.5804C0.161766 16.5443 0.187017 16.5106 0.214171 16.4792C0.369065 16.2223 0.664139 16.0658 0.981021 16.1032C2.58326 16.2923 4.18506 16.0967 5.59691 15.479C4.19658 14.8603 3.10953 13.6615 2.63818 12.1886C2.55337 11.9236 2.61422 11.6334 2.79831 11.4249C2.80469 11.4177 2.81119 11.4106 2.81779 11.4037C1.69615 10.4149 0.988608 8.96635 0.988608 7.35201V7.29269C0.988608 7.01309 1.13633 6.75437 1.37692 6.61259C1.45597 6.56601 1.54116 6.53434 1.62859 6.51763C1.24377 5.7741 1.02612 4.92946 1.02612 4.03473C1.02612 3.21423 1.03081 2.18017 1.6076 1.24888C1.7369 1.04011 1.94765 0.912819 2.17316 0.882652C2.50487 0.778176 2.88122 0.878693 3.11437 1.16508C5.02477 3.51164 7.78243 5.13284 10.9212 5.56029L10.9207 5.48434ZM7.92347 18.2465C6.54515 18.2465 5.22163 18.0168 3.98689 17.5938C5.48825 17.3334 6.92888 16.7409 8.17023 15.7665C8.43268 15.5604 8.53721 15.2112 8.4312 14.8945C8.32519 14.5779 8.03161 14.3624 7.69822 14.3565C6.4678 14.3347 5.37796 13.7271 4.69666 12.8001C5.02988 12.7731 5.35549 12.7158 5.67088 12.6304C6.02376 12.5348 6.26464 12.2086 6.25258 11.8426C6.24052 11.4767 5.97871 11.1671 5.6203 11.095C4.25729 10.821 3.15773 9.81775 2.7426 8.50985C3.09422 8.5949 3.45916 8.6455 3.83344 8.65777C4.18497 8.66929 4.50153 8.446 4.60916 8.11059C4.71679 7.7752 4.58941 7.40896 4.29701 7.21316C3.27398 6.5281 2.60163 5.3587 2.60163 4.03473C2.60163 3.74111 2.60576 3.47907 2.62333 3.24147C4.9999 5.66465 8.25171 7.22555 11.8704 7.40805C12.1441 7.42185 12.4085 7.30643 12.5848 7.09618C12.761 6.88593 12.8287 6.60513 12.7678 6.33748C12.7048 6.06102 12.6713 5.77637 12.6713 5.48434C12.6713 3.42324 14.3388 1.75346 16.394 1.75346C18.5548 1.75346 20.1325 3.75857 20.1325 6.01827C20.1325 11.7281 15.7992 18.2465 7.92347 18.2465Z"
      fill="currentColor"
    />
  </svg>
);

export const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
    <path d="M19.71,18.29,16,14.61A9,9,0,1,0,14.61,16l3.68,3.68a1,1,0,0,0,1.42,0A1,1,0,0,0,19.71,18.29ZM2,9a7,7,0,1,1,12,4.93h0s0,0,0,0A7,7,0,0,1,2,9Z" />
  </svg>
);

export const AddPhotoIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M21 6H17.83L16 4H10V6H15.12L16.95 8H21V20H5V11H3V20C3 21.1 3.9 22 5 22H21C22.1 22 23 21.1 23 20V8C23 6.9 22.1 6 21 6ZM8 14C8 16.76 10.24 19 13 19C15.76 19 18 16.76 18 14C18 11.24 15.76 9 13 9C10.24 9 8 11.24 8 14ZM13 11C14.65 11 16 12.35 16 14C16 15.65 14.65 17 13 17C11.35 17 10 15.65 10 14C10 12.35 11.35 11 13 11ZM5 6H8V4H5V1H3V4H0V6H3V9H5V6Z"
      fill="currentColor"
    />
  </svg>
);

export const RightArrowIcon = () => (
  <svg width="7" height="9" viewBox="0 0 7 9" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0.19 7.166L4.586 4.786V4.674L0.19 2.294V0.586L6.21 3.89V5.57L0.19 8.874V7.166Z"
      fill="currentColor"
    />
  </svg>
);

export const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9.50419 1.33752C9.73199 1.10971 10.1013 1.10971 10.3291 1.33752L12.6625 3.67085C12.8903 3.89866 12.8903 4.268 12.6625 4.49581L5.07915 12.0791C4.96975 12.1885 4.82138 12.25 4.66667 12.25H2.33333C2.01117 12.25 1.75 11.9888 1.75 11.6667V9.33333C1.75 9.17862 1.81146 9.03025 1.92085 8.92085L7.75407 3.08764L9.50419 1.33752ZM8.16667 4.32496L2.91667 9.57495V11.0833H4.42504L9.67504 5.83333L8.16667 4.32496ZM10.5 5.00837L11.425 4.08333L9.91667 2.57496L8.99162 3.5L10.5 5.00837Z"
      fill="currentColor"
    />
  </svg>
);

export const FileIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="19" height="22" viewBox="0 0 19 22" fill="none">
    <path
      d="M5.18182 15.2308C4.70484 15.2308 4.31818 15.6096 4.31818 16.0769C4.31818 16.5442 4.70484 16.9231 5.18182 16.9231H10.3636C10.8406 16.9231 11.2273 16.5442 11.2273 16.0769C11.2273 15.6096 10.8406 15.2308 10.3636 15.2308H5.18182Z"
      fill="currentColor"
    />
    <path
      d="M4.31818 11C4.31818 10.5327 4.70484 10.1538 5.18182 10.1538H13.8182C14.2952 10.1538 14.6818 10.5327 14.6818 11C14.6818 11.4673 14.2952 11.8462 13.8182 11.8462H5.18182C4.70484 11.8462 4.31818 11.4673 4.31818 11Z"
      fill="currentColor"
    />
    <path
      d="M5.18182 5.07692C4.70484 5.07692 4.31818 5.45576 4.31818 5.92308C4.31818 6.39039 4.70484 6.76923 5.18182 6.76923H13.8182C14.2952 6.76923 14.6818 6.39039 14.6818 5.92308C14.6818 5.45576 14.2952 5.07692 13.8182 5.07692H5.18182Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.76097 9.9273e-07C3.2289 -1.95847e-05 2.76999 -3.73311e-05 2.39416 0.0349734C1.99632 0.0720346 1.60335 0.154215 1.23717 0.374069C0.888545 0.58338 0.595434 0.870557 0.381799 1.21212C0.157403 1.57089 0.0735239 1.95591 0.0356968 2.3457C-3.64455e-05 2.71391 -1.91299e-05 3.16351 9.46947e-07 3.6848V18.3152C-1.91299e-05 18.8365 -3.64455e-05 19.2861 0.0356968 19.6543C0.0735238 20.0441 0.157403 20.4291 0.381799 20.7879C0.595434 21.1294 0.888545 21.4166 1.23717 21.6259C1.60335 21.8458 1.99632 21.928 2.39416 21.965C2.76999 22 3.2289 22 3.76098 22H15.239C15.7711 22 16.23 22 16.6058 21.965C17.0037 21.928 17.3967 21.8458 17.7628 21.6259C18.1115 21.4166 18.4046 21.1294 18.6182 20.7879C18.8426 20.4291 18.9265 20.0441 18.9643 19.6543C19 19.2861 19 18.8365 19 18.3152V3.68485C19 3.16354 19 2.71392 18.9643 2.3457C18.9265 1.95591 18.8426 1.57089 18.6182 1.21212C18.4046 0.870557 18.1115 0.58338 17.7628 0.374069C17.3967 0.154215 17.0037 0.0720347 16.6058 0.0349734C16.23 -3.74383e-05 15.7711 -1.95846e-05 15.239 9.9273e-07H3.76097ZM2.13966 1.817C2.18884 1.78747 2.28625 1.74497 2.55766 1.71968C2.84192 1.6932 3.21787 1.69231 3.8 1.69231H15.2C15.7821 1.69231 16.1581 1.6932 16.4423 1.71968C16.7138 1.74497 16.8112 1.78747 16.8603 1.817C16.9765 1.88677 17.0742 1.98249 17.1455 2.09635C17.1756 2.14453 17.219 2.23997 17.2448 2.50588C17.2718 2.78439 17.2727 3.15273 17.2727 3.72308V18.2769C17.2727 18.8473 17.2718 19.2156 17.2448 19.4941C17.219 19.76 17.1756 19.8555 17.1455 19.9037C17.0742 20.0175 16.9765 20.1132 16.8603 20.183C16.8112 20.2125 16.7138 20.255 16.4423 20.2803C16.1581 20.3068 15.7821 20.3077 15.2 20.3077H3.8C3.21787 20.3077 2.84192 20.3068 2.55766 20.2803C2.28625 20.255 2.18884 20.2125 2.13966 20.183C2.02346 20.1132 1.92575 20.0175 1.85454 19.9037C1.82441 19.8555 1.78102 19.76 1.75521 19.4941C1.72819 19.2156 1.72728 18.8473 1.72728 18.2769V3.72308C1.72728 3.15273 1.72819 2.78439 1.75521 2.50588C1.78102 2.23997 1.82441 2.14453 1.85454 2.09635C1.92575 1.98249 2.02346 1.88677 2.13966 1.817Z"
      fill="currentColor"
    />
  </svg>
);

export const AudioIcon = () => (
  <svg width="19" height="19" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.375 0.375H9.625V4.375H10.625V10.625H6.375V4.375H8.375V1.625H2.625V4.375H4.625V10.625H0.375V4.375H1.375V0.375ZM1.625 5.625V9.375H3.375V5.625H1.625ZM9.375 5.625H7.625V9.375H9.375V5.625Z"
      fill="currentColor"
    />
  </svg>
);

export const TextBlockIcon = () => (
  <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.25077 3.76363e-07C5.54518 -0.000301271 5.80937 0.180745 5.91534 0.455422L10.4831 12.2952C10.6246 12.6619 10.442 13.0737 10.0754 13.2152C9.70875 13.3566 9.29688 13.1741 9.15543 12.8075L7.6596 8.9302H2.86223L1.37608 12.8061C1.23539 13.173 0.823898 13.3564 0.45698 13.2157C0.0900621 13.075 -0.093333 12.6635 0.0473556 12.2966L4.58714 0.45679C4.69254 0.181895 4.95636 0.000304942 5.25077 3.76363e-07ZM5.25354 2.69362L7.19294 7.7206H3.32603L5.25354 2.69362Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.5098 3.49649C13.1749 3.49649 11.3975 5.60071 11.3975 8.3387C11.3975 11.219 13.3172 13.1809 15.5098 13.1809C16.6802 13.1809 17.894 12.6925 18.7192 11.7713V12.4944C18.7192 12.8481 19.006 13.1348 19.3596 13.1348C19.7133 13.1348 20 12.8481 20 12.4944V4.22647C20 3.87281 19.7133 3.5861 19.3596 3.5861C19.006 3.5861 18.7192 3.87281 18.7192 4.22647V4.94251C17.9382 4.05706 16.7812 3.49649 15.5098 3.49649ZM18.7192 10.03V6.47767C18.1638 5.45175 16.9933 4.70685 15.7771 4.70685C14.3854 4.70685 12.8574 5.99933 12.8574 8.33846C12.8574 10.5353 14.2431 11.9701 15.7771 11.9701C17.0341 11.9701 18.1752 11.0219 18.7192 10.03Z"
      fill="currentColor"
    />
  </svg>
);

export const ImageBlockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9 3.80661C7.66117 3.80661 6.57584 4.89194 6.57584 6.23077C6.57584 7.56959 7.66117 8.65493 9 8.65493C10.3388 8.65493 11.4242 7.56959 11.4242 6.23077C11.4242 4.89194 10.3388 3.80661 9 3.80661ZM7.822 6.23077C7.822 5.58018 8.34941 5.05276 9 5.05276C9.65059 5.05276 10.178 5.58018 10.178 6.23077C10.178 6.88136 9.65059 7.40877 9 7.40877C8.34941 7.40877 7.822 6.88136 7.822 6.23077Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.07692 0H15.9231C17.0701 0 18 0.92987 18 2.07692V15.9231C18 17.0701 17.0701 18 15.9231 18H2.07692C0.929871 18 0 17.0701 0 15.9231V2.07692C0 0.929871 0.92987 0 2.07692 0ZM2.07692 1.38462C1.69457 1.38462 1.38462 1.69457 1.38462 2.07692V10.1958L3.71326 7.86711C3.83274 7.74763 3.99562 7.6818 4.16456 7.68471C4.3335 7.68761 4.49401 7.759 4.60931 7.88252L9.51722 13.1401L13.4056 9.25173C13.6489 9.0084 14.0434 9.0084 14.2867 9.25173L16.6154 11.5804V2.07692C16.6154 1.69457 16.3054 1.38462 15.9231 1.38462H2.07692ZM1.38462 15.9231V11.9581L4.13843 9.20427L9.04307 14.4583L10.9954 16.6154H2.07692C1.69457 16.6154 1.38462 16.3054 1.38462 15.9231ZM15.9231 16.6154H12.6762L10.3616 14.0581L13.8462 10.5735L16.6154 13.3427V15.9231C16.6154 16.3054 16.3054 16.6154 15.9231 16.6154Z"
      fill="currentColor"
    />
  </svg>
);

export const LinkBlockIcon = () => (
  <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.557 2.73506C11.5976 2.02815 12.9133 2.01431 13.8908 2.65626C14.1128 2.80203 14.3498 3.03338 14.9835 3.66708C15.6173 4.30079 15.8486 4.53784 15.9944 4.7598C16.6363 5.73731 16.6225 7.05302 15.9156 8.09362C15.7542 8.33112 15.5045 8.5857 14.8476 9.24267L13.8956 10.1946C13.5952 10.495 13.5952 10.9821 13.8956 11.2825C14.196 11.5829 14.6831 11.5829 14.9835 11.2825L16.0015 10.2645C16.5702 9.69601 16.9342 9.33219 17.1882 8.95818C18.2271 7.4289 18.2807 5.43847 17.2804 3.91524C17.0364 3.54375 16.6809 3.18832 16.1402 2.64784L16.0028 2.51046C15.4623 1.96977 15.1069 1.61421 14.7354 1.37024C13.2122 0.369895 11.2217 0.423522 9.69246 1.4624C9.31844 1.71648 8.95461 2.08041 8.38606 2.64913L7.36812 3.66708C7.0677 3.9675 7.0677 4.45458 7.36812 4.755C7.66854 5.05542 8.15562 5.05542 8.45604 4.755L9.40797 3.80307C10.0649 3.14611 10.3195 2.8964 10.557 2.73506Z"
      fill="currentColor"
    />
    <path
      d="M4.10437 9.10668C4.40479 8.80626 4.40479 8.31918 4.10437 8.01876C3.80395 7.71834 3.31687 7.71834 3.01645 8.01876L1.9985 9.0367C1.42977 9.60525 1.06584 9.96907 0.811762 10.3431C-0.227112 11.8724 -0.28074 13.8628 0.719608 15.386C0.963576 15.7575 1.31914 16.1129 1.85984 16.6534L1.99719 16.7908C2.53768 17.3315 2.89311 17.6871 3.2646 17.931C4.78784 18.9314 6.77826 18.8777 8.30754 17.8389C8.68156 17.5848 9.04538 17.2209 9.61392 16.6522L10.6319 15.6342C10.9323 15.3338 10.9323 14.8467 10.6319 14.5463C10.3315 14.2458 9.84438 14.2458 9.54396 14.5463L8.59203 15.4982C7.93507 16.1552 7.68049 16.4049 7.44299 16.5662C6.40238 17.2731 5.08667 17.287 4.10916 16.645C3.8872 16.4992 3.65016 16.2679 3.01645 15.6342C2.38274 15.0005 2.1514 14.7634 2.00563 14.5415C1.36367 13.564 1.37752 12.2483 2.08443 11.2076C2.24577 10.9701 2.49548 10.7156 3.15244 10.0586L4.10437 9.10668Z"
      fill="currentColor"
    />
    <path
      d="M12.2645 7.47406C12.5649 7.17364 12.5649 6.68656 12.2645 6.38614C11.964 6.08572 11.477 6.08572 11.1765 6.38614L5.73696 11.8257C5.43654 12.1262 5.43654 12.6132 5.73696 12.9136C6.03738 13.2141 6.52446 13.2141 6.82488 12.9136L12.2645 7.47406Z"
      fill="currentColor"
    />
  </svg>
);

export const ColorBlockIcon = () => (
  <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 10.877C0 5.35411 4.47716 0.876953 10 0.876953C15.5228 0.876953 20 5.35411 20 10.877C20 16.3998 15.5228 20.877 10 20.877C4.47716 20.877 0 16.3998 0 10.877ZM4.38525 4.40813C5.74664 3.22546 7.48503 2.46544 9.39615 2.33234V9.41903L4.38525 4.40813ZM3.53111 5.26228C2.34846 6.62368 1.58846 8.36207 1.45538 10.2732H8.542L3.53111 5.26228ZM8.542 11.4811H1.45541C1.58857 13.3922 2.34862 15.1305 3.53129 16.4918L8.542 11.4811ZM4.38546 17.3459C5.74682 18.5285 7.48513 19.2885 9.39615 19.4216V12.3353L4.38546 17.3459ZM10.6041 12.3353V19.4215C12.5151 19.2884 14.2533 18.5284 15.6147 17.3458L10.6041 12.3353ZM16.4688 16.4917C17.6514 15.1304 18.4114 13.3921 18.5446 11.4811H11.4582L16.4688 16.4917ZM11.4582 10.2732H18.5446C18.4115 8.36213 17.6516 6.62379 16.469 5.26241L11.4582 10.2732ZM15.6149 4.40825C14.2535 3.22556 12.5152 2.46551 10.6041 2.33236V9.41903L15.6149 4.40825Z"
      fill="currentColor"
    />
  </svg>
);

export const QuoteBlockIcon = () => (
  <svg width="20" height="17" viewBox="0 0 20 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.1692 1.01208C14.2886 0.648795 15.5789 0.655294 16.6352 1.19808C19.0721 2.45028 20.1265 5.67767 19.1456 9.09737C18.7056 10.6314 17.9317 12.1209 16.2927 13.6243C14.6537 15.1277 12.3805 16.0146 11.3259 16.0146C10.948 16.0146 10.6418 15.7168 10.6418 15.3494C10.6418 14.9821 10.9547 14.6843 11.3259 14.6843C12.5306 14.6843 13.6071 14.0287 14.8817 13.0631C15.8574 12.3241 16.6191 11.3788 17.0409 10.5551C17.8477 8.97973 17.875 6.75928 16.5715 6.16914C15.9455 6.85295 15.1281 7.25185 14.1487 7.25185C12.0204 7.25185 10.7661 5.5397 10.82 3.9895C10.8716 2.50791 11.8129 1.45226 13.1692 1.01208ZM2.52745 1.01208C3.64682 0.648795 4.93716 0.655294 5.99348 1.19808C8.43038 2.45028 9.48473 5.67767 8.50384 9.09737C8.06384 10.6314 7.28991 12.1209 5.65093 13.6243C4.01195 15.1277 1.73875 16.0146 0.684097 16.0146C0.306281 16.0146 0 15.7168 0 15.3494C0 14.9821 0.312936 14.6843 0.684097 14.6843C1.88886 14.6843 2.9653 14.0287 4.23998 13.0631C5.2156 12.3241 5.97735 11.3788 6.39916 10.5551C7.2059 8.97973 7.23319 6.75928 5.92974 6.16914C5.30378 6.85295 4.48634 7.25185 3.5069 7.25185C1.37863 7.25185 0.124322 5.5397 0.178259 3.9895C0.229809 2.50791 1.17117 1.45226 2.52745 1.01208Z"
      fill="currentColor"
    />
  </svg>
);

export const CodeBlockIcon = () => (
  <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.7911 1.35047C13.9489 0.956021 13.7571 0.508351 13.3626 0.350572C12.9682 0.192792 12.5205 0.384651 12.3627 0.779099L6.20886 16.1637C6.05108 16.5582 6.24294 17.0058 6.63739 17.1636C7.03184 17.3214 7.47951 17.1295 7.63729 16.7351L13.7911 1.35047Z"
      fill="currentColor"
    />
    <path
      d="M4.39008 5.13624C4.69049 5.43664 4.69049 5.92369 4.39008 6.2241L1.85709 8.75709L4.39008 11.2901C4.69049 11.5905 4.69049 12.0775 4.39008 12.3779C4.08968 12.6783 3.60263 12.6783 3.30223 12.3779L0.225302 9.30102C-0.0751008 9.00062 -0.0751008 8.51357 0.225302 8.21316L3.30223 5.13624C3.60263 4.83584 4.08968 4.83584 4.39008 5.13624Z"
      fill="currentColor"
    />
    <path
      d="M15.6099 5.13624C15.9103 4.83584 16.3974 4.83584 16.6978 5.13624L19.7747 8.21316C20.0751 8.51357 20.0751 9.00062 19.7747 9.30102L16.6978 12.3779C16.3974 12.6783 15.9103 12.6783 15.6099 12.3779C15.3095 12.0775 15.3095 11.5905 15.6099 11.2901L18.1429 8.75709L15.6099 6.2241C15.3095 5.92369 15.3095 5.43664 15.6099 5.13624Z"
      fill="currentColor"
    />
  </svg>
);

export const MonoBlockIcon = () => (
  <svg width="20" height="14" viewBox="0 0 18 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M7.24057 11.7323L6.29112 8.52988H2.3485L1.39906 11.7323H-0.000976562L3.44278 0.499817H5.22903L8.67278 11.7323H7.24057ZM4.39223 1.93203H4.2474L2.67035 7.35515H5.96927L4.39223 1.93203Z"
      fill="currentColor"
    />
    <path
      d="M17.115 11.7323C16.6107 11.7323 16.2513 11.6035 16.0368 11.346C15.8222 11.0886 15.6881 10.7667 15.6345 10.3805H15.554C15.3716 10.8633 15.0766 11.2441 14.6689 11.5231C14.272 11.7913 13.7356 11.9254 13.0597 11.9254C12.1907 11.9254 11.4987 11.7001 10.9838 11.2495C10.4688 10.7989 10.2114 10.182 10.2114 9.39887C10.2114 8.62644 10.4903 8.03102 11.0482 7.61262C11.6168 7.19422 12.5287 6.98502 13.7839 6.98502H15.554V6.16431C15.554 5.55281 15.3824 5.09686 15.0391 4.79647C14.6958 4.48535 14.2076 4.32979 13.5747 4.32979C13.0168 4.32979 12.5608 4.44244 12.2068 4.66773C11.8528 4.88229 11.5685 5.17732 11.3539 5.55281L10.4849 4.90911C10.5922 4.69455 10.737 4.48535 10.9194 4.28151C11.1018 4.07768 11.3271 3.90066 11.5953 3.75047C11.8635 3.58954 12.1693 3.46617 12.5126 3.38034C12.8559 3.28379 13.2367 3.23551 13.6551 3.23551C14.6314 3.23551 15.4038 3.48226 15.9724 3.97576C16.5517 4.46926 16.8414 5.15586 16.8414 6.03558V10.6058H18V11.7323H17.115ZM13.285 10.8472C13.6176 10.8472 13.918 10.8096 14.1862 10.7345C14.4651 10.6487 14.7065 10.5414 14.9103 10.4127C15.1142 10.2732 15.2697 10.1123 15.377 9.92992C15.495 9.73681 15.554 9.53297 15.554 9.31841V7.95056H13.7839C13.0114 7.95056 12.4482 8.05784 12.0942 8.27241C11.7401 8.48697 11.5631 8.79809 11.5631 9.20576V9.5437C11.5631 9.97283 11.7133 10.3 12.0137 10.5253C12.3248 10.7399 12.7486 10.8472 13.285 10.8472Z"
      fill="currentColor"
    />
  </svg>
);

export const HeadingIcon = () => (
  <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9.33634 0C9.0481 0 8.81443 0.237608 8.81443 0.530713C8.81443 0.823817 9.0481 1.06143 9.33634 1.06143H11.2629V7.46905H3.72428V1.06143H5.66366C5.9519 1.06143 6.18557 0.823817 6.18557 0.530713C6.18557 0.237608 5.9519 0 5.66366 0H0.521907C0.233666 0 0 0.237608 0 0.530713C0 0.823817 0.233666 1.06143 0.521907 1.06143H2.44851V14.9386H0.521907C0.233666 14.9386 0 15.1762 0 15.4693C0 15.7624 0.233666 16 0.521907 16H5.66366C5.9519 16 6.18557 15.7624 6.18557 15.4693C6.18557 15.1762 5.9519 14.9386 5.66366 14.9386H3.72428V8.53047H11.2629V14.9386H9.33634C9.0481 14.9386 8.81443 15.1762 8.81443 15.4693C8.81443 15.7624 9.0481 16 9.33634 16H14.4781C14.7663 16 15 15.7624 15 15.4693C15 15.1762 14.7663 14.9386 14.4781 14.9386H12.5387V1.06143H14.4781C14.7663 1.06143 15 0.823817 15 0.530713C15 0.237608 14.7663 0 14.4781 0H9.33634Z"
      fill="currentColor"
    />
  </svg>
);

export const NavArrow = ({ back, style }: { back?: boolean } & StyleProp) => (
  <svg
    width="21"
    height="18"
    viewBox="0 0 21 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={back ? { ...style, transform: "rotate(180deg)" } : { ...style }}
  >
    <path
      d="M11.7341 0.292893C12.1069 -0.0976311 12.7113 -0.0976311 13.0841 0.292893L20.7204 8.29289C21.0932 8.68342 21.0932 9.31658 20.7204 9.70711L13.0841 17.7071C12.7113 18.0976 12.1069 18.0976 11.7341 17.7071C11.3614 17.3166 11.3614 16.6834 11.7341 16.2929L17.741 10H0.954545C0.427365 10 0 9.55229 0 9C0 8.44772 0.427365 8 0.954545 8H17.741L11.7341 1.70711C11.3614 1.31658 11.3614 0.683417 11.7341 0.292893Z"
      fill="currentColor"
    />
  </svg>
);

export const Shared = ({ style }: StyleProp) => (
  <svg width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg" style={style}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 0H10.8889V13H0V0ZM1.55556 1.73333V11.2667H9.33333V1.73333H1.55556ZM12.4444 1.73333H11.6667V0H14V12.1333H11.6667V10.4H12.4444V1.73333ZM4.66667 6.06667H3.11111V4.33333H4.66667V6.06667ZM7.77778 6.06667H6.22222V4.33333H7.77778V6.06667ZM7.77778 10.4H5.44444V8.66667H7.77778V10.4Z"
      fill="currentColor"
    />
  </svg>
);

export const HelpButton = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.50006 3.25C5.93512 3.25072 5.39353 3.47545 4.99406 3.87492C4.59459 4.27439 4.36986 4.81598 4.36914 5.38092H5.45247C5.45247 4.80296 5.92264 4.33333 6.50006 4.33333C7.07747 4.33333 7.54764 4.80296 7.54764 5.38092C7.54764 5.70483 7.2871 5.93992 6.88897 6.26167C6.75891 6.36355 6.634 6.47184 6.51468 6.58613C5.9741 7.12617 5.95839 7.69979 5.95839 7.76371V8.125H7.04172L7.04118 7.78212C7.04172 7.77346 7.05906 7.57304 7.28006 7.35258C7.36131 7.27133 7.46368 7.19008 7.56985 7.1045C7.99181 6.76271 8.63043 6.2465 8.63043 5.38092C8.63 4.81599 8.40543 4.27431 8.00602 3.8748C7.6066 3.47528 7.06499 3.25057 6.50006 3.25ZM5.95839 8.66667H7.04172V9.75H5.95839V8.66667Z"
      fill="currentColor"
    />
    <path
      d="M6.50004 1.08301C3.51329 1.08301 1.08337 3.51292 1.08337 6.49967C1.08337 9.48642 3.51329 11.9163 6.50004 11.9163C9.48679 11.9163 11.9167 9.48642 11.9167 6.49967C11.9167 3.51292 9.48679 1.08301 6.50004 1.08301ZM6.50004 10.833C4.11075 10.833 2.16671 8.88897 2.16671 6.49967C2.16671 4.11038 4.11075 2.16634 6.50004 2.16634C8.88933 2.16634 10.8334 4.11038 10.8334 6.49967C10.8334 8.88897 8.88933 10.833 6.50004 10.833Z"
      fill="currentColor"
    />
  </svg>
);

export const Locked = () => (
  <svg width="13" height="14" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.18047 5.43912H4.93787H8.45266H9.62426M3.18047 5.43912H1V9V12.9967H9.23373L12 13V5.44241L10.0148 5.43912H9.62426M3.18047 5.43912V1H4.38462H9.62426V1.88889V2.77419L9.62426 5.43912"
      stroke="currentColor"
      strokeWidth="1.3"
    />
  </svg>
);

export const Unlocked = () => (
  <svg width="13" height="14" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.18047 5.43912H4.93787H8.45266H9.62426H10.0148L12 5.44241V13L9.23373 12.9967H1V9V5.43912H3.18047ZM3.18047 5.43912V1H4.38462H9.62426V3.5"
      stroke="currentColor"
      strokeWidth="1.3"
    />
  </svg>
);

export const Download = () => (
  <svg width="8" height="10" viewBox="0 0 8 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 0V9M4 9L1 6M4 9L7 6" stroke="currentColor" />
  </svg>
);

export const Check = () => (
  <svg width="13" height="10" viewBox="0 0 13 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.7576 0.180429C13.0557 0.442514 13.0826 0.893993 12.8176 1.18884L5.11387 9.76026C4.97682 9.91275 4.78037 10 4.57408 10C4.36778 10 4.17134 9.91275 4.03428 9.76026L0.182434 5.47455C-0.0825619 5.1797 -0.0557092 4.72822 0.242411 4.46614C0.540532 4.20406 0.997028 4.23061 1.26202 4.52546L4.57408 8.21057L11.738 0.239748C12.003 -0.055097 12.4595 -0.0816547 12.7576 0.180429Z"
      fill="currentColor"
    />
  </svg>
);

export const AppleIcon = () => (
  <svg
    width="11px"
    height="11px"
    viewBox="0 0 256 315"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="xMidYMid"
  >
    <g>
      <path
        d="M213.803394,167.030943 C214.2452,214.609646 255.542482,230.442639 256,230.644727 C255.650812,231.761357 249.401383,253.208293 234.24263,275.361446 C221.138555,294.513969 207.538253,313.596333 186.113759,313.991545 C165.062051,314.379442 158.292752,301.507828 134.22469,301.507828 C110.163898,301.507828 102.642899,313.596301 82.7151126,314.379442 C62.0350407,315.16201 46.2873831,293.668525 33.0744079,274.586162 C6.07529317,235.552544 -14.5576169,164.286328 13.147166,116.18047 C26.9103111,92.2909053 51.5060917,77.1630356 78.2026125,76.7751096 C98.5099145,76.3877456 117.677594,90.4371851 130.091705,90.4371851 C142.497945,90.4371851 165.790755,73.5415029 190.277627,76.0228474 C200.528668,76.4495055 229.303509,80.1636878 247.780625,107.209389 C246.291825,108.132333 213.44635,127.253405 213.803394,167.030988 M174.239142,50.1987033 C185.218331,36.9088319 192.607958,18.4081019 190.591988,0 C174.766312,0.636050225 155.629514,10.5457909 144.278109,23.8283506 C134.10507,35.5906758 125.195775,54.4170275 127.599657,72.4607932 C145.239231,73.8255433 163.259413,63.4970262 174.239142,50.1987249"
        fill="currentColor"
      ></path>
    </g>
  </svg>
);

export const Expand = ({ style }: StyleProp) => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" style={style}>
    <path d="M2.59998 1H8.99998V7.4M8.5 1.5L1 9" stroke="currentColor" strokeWidth="1.3" />
  </svg>
);

export const Like = ({ style }: StyleProp) => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" style={style}>
    <path
      d="M2.56809 10L1.45914 9.15323L3.8716 5.72581L0 4.39516L0.40856 3.06452L4.28016 4.35484V0H5.71984V4.35484L9.59144 3.06452L10 4.39516L6.1284 5.72581L8.54086 9.15323L7.43191 10L5 6.53226L2.56809 10Z"
      fill="currentColor"
    />
  </svg>
);

export const Ellipsis = () => (
  <svg width="9" height="3" viewBox="0 0 9 3" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0.75 0.75H2.25V2.25H0.75V0.75Z" fill="currentColor" />
    <path d="M3.75 0.75H5.25V2.25H3.75V0.75Z" fill="currentColor" />
    <path d="M6.75 0.75H8.25V2.25H6.75V0.75Z" fill="currentColor" />
  </svg>
);

export const Connect = ({ style }: StyleProp) => (
  <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" style={style}>
    <path d="M0 2.19178H7.43363V3.61644H0V2.19178Z" fill="currentColor" />
    <path d="M0 0H7.43363V1.42466H0V0Z" fill="currentColor" />
    <path d="M0 4.38356H5.30973V5.80822H0V4.38356Z" fill="currentColor" />
    <path d="M8.49557 8V2.19178H9.87611V8H8.49557Z" fill="currentColor" />
    <path d="M6.37168 4.38357H12V5.80823H6.37168V4.38357Z" fill="currentColor" />
  </svg>
);

export const Windows = () => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0 1.55025L4.46088 0.942725L4.46282 5.2456L0.004075 5.27099L0 1.55025ZM4.45875 5.74137L4.46221 10.048L0.0034625 9.43499L0.0032125 5.71249L4.45875 5.74137ZM4.99951 0.86325L10.9143 0V5.19088L4.99951 5.23783V0.86325ZM10.9156 5.78188L10.9143 10.9494L4.9995 10.1146L4.99121 5.7722L10.9156 5.78188Z"
      fill="currentColor"
    />
  </svg>
);

const width = 69;
const height = 69;

export const YoutTubePlayButton = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    version="1.1"
    width={width}
    height={height}
    viewBox={`0 0 ${width} ${height}`}
    xmlSpace="preserve"
  >
    <defs></defs>
    <g
      style={{
        stroke: "none",
        strokeWidth: "0",
        strokeDasharray: "none",
        strokeLinecap: "butt",
        strokeLinejoin: "miter",
        strokeMiterlimit: "10",
        fill: "none",
        fillRule: "nonzero",
        opacity: "1",
      }}
      transform={`translate(1.4065934065934016 0) scale(${(2.81 / 256) * width} ${(2.81 / 256) * height})`}
    >
      <path
        d="M 88.119 23.338 c -1.035 -3.872 -4.085 -6.922 -7.957 -7.957 C 73.144 13.5 45 13.5 45 13.5 s -28.144 0 -35.162 1.881 c -3.872 1.035 -6.922 4.085 -7.957 7.957 C 0 30.356 0 45 0 45 s 0 14.644 1.881 21.662 c 1.035 3.872 4.085 6.922 7.957 7.957 C 16.856 76.5 45 76.5 45 76.5 s 28.144 0 35.162 -1.881 c 3.872 -1.035 6.922 -4.085 7.957 -7.957 C 90 59.644 90 45 90 45 S 90 30.356 88.119 23.338 z"
        style={{
          stroke: "none",
          strokeWidth: "1",
          strokeDasharray: "none",
          strokeLinecap: "butt",
          strokeLinejoin: "miter",
          strokeMiterlimit: "10",
          fill: "rgb(20,20,20)",
          fillRule: "nonzero",
          opacity: 0.7,
        }}
        transform=" matrix(1 0 0 1 0 0) "
        strokeLinecap="round"
      />
      <polygon
        points="36,58.5 59.38,45 36,31.5 "
        style={{
          stroke: "none",
          strokeWidth: "1",
          strokeDasharray: "none",
          strokeLinecap: "butt",
          strokeLinejoin: "miter",
          strokeMiterlimit: "10",
          fill: "rgb(255,255,255)",
          fillRule: "nonzero",
          opacity: "1",
        }}
        transform="  matrix(1 0 0 1 0 0) "
      />
    </g>
  </svg>
);

export const DeleteBlock = styled.div`
  background-color: ${({ theme }) => theme.primary};
  height: 1px;
  width: 7px;
`;

export const SendIcon = () => {
  return (
    <svg width="19" height="15" viewBox="0 0 19 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0 10.15L5.7 5.30026V14.9997L0 10.15Z" fill="currentColor" />
      <path d="M5.7 9.45H19V11.55H5.7V9.45Z" fill="currentColor" />
      <path d="M17.1 0H19V11.55H17.1V0Z" fill="currentColor" />
    </svg>
  );
};

export const AnimatedCaret = ({ open }: { open: boolean }) => {
  return (
    <motion.div
      style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      initial={{ rotate: open ? 180 : 0 }}
      animate={{ rotate: open ? 180 : 0 }}
      transition={{
        type: "spring",
        damping: 30,
        stiffness: 300,
      }}
    >
      <CaretIcon />
    </motion.div>
  );
};

export const CaretIcon = () => {
  return (
    <svg width="25" height="15" viewBox="0 0 28 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13.8805 0L26.8805 15H0.880493L13.8805 0Z" fill="currentColor" />
    </svg>
  );
};
